<template>
  <div>
    <div class="d-none d-md-flex justify-space-between">
      <div class="flex-1">
        <div class="text-4 d-flex align-center">
          <span>Your Balance</span>
          <div
            class="migration-required ml-4"
            v-if="vaultInfo.migrationRequired"
          >
            <v-icon x-small class="white--text-abs">fas fa-info-circle</v-icon>
            <span class="ml-1 text-6">MIGRATION REQUIRED</span>
          </div>
          <div
            class="migration-required ml-4"
            v-if="vaultInfo.withdrawalRequired"
          >
            <v-icon x-small class="white--text-abs">fas fa-info-circle</v-icon>
            <span class="ml-1 text-6">WITHDRAWAL REQUIRED</span>
          </div>
        </div>
        <div class="mt-4">
          <div
            class="d-flex align-center bg-vaults-lighter has-border border-vaults rounded-8 gray--text text-6 font-weight-medium px-6 py-2"
          >
            <div class="flex-3 d-flex align-center">
              <span>Available</span>
              <v-tooltip top max-width="550" color="dark">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1 icongray--text"
                    v-bind="attrs"
                    v-on="on"
                    >fas fa-info-circle</v-icon
                  >
                </template>
                <div>
                  Your amount of assets available to deposit
                </div>
              </v-tooltip>
            </div>
            <div class="flex-3 d-flex justify-end align-center">
              <span>Shares</span>
              <v-tooltip top max-width="550" color="dark">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1 icongray--text"
                    v-bind="attrs"
                    v-on="on"
                    >fas fa-info-circle</v-icon
                  >
                </template>
                <div>
                  Your deposit share of total value locked in the vault
                </div>
              </v-tooltip>
            </div>
            <div class="flex-3 d-flex justify-end align-center">
              <span>Total Deposits</span>
              <v-tooltip top max-width="550" color="dark">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1 icongray--text"
                    v-bind="attrs"
                    v-on="on"
                    >fas fa-info-circle</v-icon
                  >
                </template>
                <div>
                  Your total assets deposited
                </div>
              </v-tooltip>
            </div>
            <div class="flex-3 d-flex justify-end align-center">
              <span>Total Assets</span>
              <v-tooltip top max-width="550" color="dark">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    x-small
                    class="ml-1 icongray--text"
                    v-bind="attrs"
                    v-on="on"
                    >fas fa-info-circle</v-icon
                  >
                </template>
                <div>
                  Your amount of Total Deposited + Available Assets
                </div>
              </v-tooltip>
            </div>
          </div>
          <div class="mt-4 d-flex align-center px-6 text-5">
            <div class="flex-3">
              <div
                v-for="(item, index) in availableBalances"
                :key="item.name"
                :class="{ 'mt-2': index > 0 }"
              >
                <div>
                  <span> {{ item.balance | denomFormat }}</span>
                  <span class="darkgray--text text-6 text-uppercase">
                    {{ item.name }}</span
                  >
                </div>
                <div class="mt-1">
                  <span class="text-6 font-weight-medium"
                    >${{ item.value | currency }}</span
                  ><span class="text-6 gray--text ml-1">USD</span>
                </div>
              </div>
            </div>
            <div class="flex-3 text-right">
              <span> {{ shares | denomFormat }}</span>
              <span class="darkgray--text text-6">
                u{{ vaultInfo.identity | uppercase }}</span
              >
            </div>
            <div class="flex-3 text-right">
              <div>
                <span> {{ expectedReturn | denomFormat }}</span>
                <span class="darkgray--text text-6">
                  {{ vaultInfo.token | uppercase }}</span
                >
              </div>
              <div class="mt-1">
                <span class="text-6 font-weight-medium"
                  >${{
                    (expectedReturn * getTokenPriceInUsd(vaultInfo.token))
                      | currency
                  }}</span
                ><span class="text-6 gray--text ml-1">USD</span>
              </div>
            </div>
            <div class="flex-3 text-right">
              <div>
                <span> {{ totalAssets | denomFormat }}</span>
                <span class="darkgray--text text-6">
                  {{ vaultInfo.token | uppercase }}</span
                >
              </div>
              <div class="mt-1">
                <span class="text-6 font-weight-medium"
                  >${{ totalAssetsUsd | currency }}</span
                ><span class="text-6 gray--text ml-1">USD</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ml-10" style="width:160px">
        <div>
          <c-btn
            class="width-100 mt-4"
            is-secondary
            :to="{
              name: 'VaultDetails',
              params: { vault: vaultInfo.identity.toLowerCase() },
              query: { action: 'withdraw' },
            }"
            :disabled="vaultInfo.withdrawalDisabled"
            walletRequired
            >Withdraw</c-btn
          >
        </div>
        <div v-if="vaultInfo.migrationRequired">
          <c-btn
            class="width-100 mt-4"
            :to="{
              name: 'VaultDetails',
              params: { vault: vaultInfo.identity.toLowerCase() },
              query: { action: 'migrate' },
            }"
            walletRequired
            >Migrate</c-btn
          >
        </div>
      </div>
    </div>
    <div class="d-block d-md-none">
      <div class="text-4 d-flex align-center">
        <span>Your Balance</span>
        <div class="migration-required ml-4" v-if="vaultInfo.migrationRequired">
          <v-icon x-small class="white--text-abs">fas fa-info-circle</v-icon>
          <span class="ml-1 text-6">MIGRATION REQUIRED</span>
        </div>
        <div
          class="migration-required ml-4"
          v-if="vaultInfo.withdrawalRequired"
        >
          <v-icon x-small class="white--text-abs">fas fa-info-circle</v-icon>
          <span class="ml-1 text-6">WITHDRAWAL REQUIRED</span>
        </div>
      </div>
      <div class="mt-8">
        <div class="d-flex justify-space-between">
          <div class="d-flex align-center">
            <span class="gray--text text-6 font-weight-medium">Available</span>
            <v-tooltip top max-width="550" color="dark">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="ml-1 icongray--text"
                  v-bind="attrs"
                  v-on="on"
                  >fas fa-info-circle</v-icon
                >
              </template>
              <div>
                Your amount of assets available to deposit
              </div>
            </v-tooltip>
          </div>
          <div
            v-for="item in availableBalances"
            :key="item.name"
            class="d-flex"
          >
            <div>
              <span> {{ item.balance | denomFormat }}</span>
              <span class="darkgray--text text-6 text-uppercase">
                {{ item.name }}</span
              >
            </div>
            <div class="ml-1">
              (<span class="text-6 font-weight-medium"
                >${{ item.value | currency }}</span
              ><span class="text-6 gray--text ml-1">USD</span>)
            </div>
          </div>
        </div>
        <div class="d-flex justify-space-between mt-2">
          <div class="d-flex align-center">
            <span class="gray--text text-6 font-weight-medium">Shares</span>
            <v-tooltip top max-width="550" color="dark">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="ml-1 icongray--text"
                  v-bind="attrs"
                  v-on="on"
                  >fas fa-info-circle</v-icon
                >
              </template>
              <div>
                Your deposit share of total value locked in the vault
              </div>
            </v-tooltip>
          </div>
          <div>
            <span> {{ shares | denomFormat }}</span>
            <span class="darkgray--text text-6">
              u{{ vaultInfo.identity | uppercase }}</span
            >
          </div>
        </div>
        <div class="d-flex justify-space-between mt-2">
          <div class="d-flex align-center">
            <span class="gray--text text-6 font-weight-medium"
              >Total Deposits</span
            >
            <v-tooltip top max-width="550" color="dark">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="ml-1 icongray--text"
                  v-bind="attrs"
                  v-on="on"
                  >fas fa-info-circle</v-icon
                >
              </template>
              <div>
                Your total assets deposited
              </div>
            </v-tooltip>
          </div>
          <div class="d-flex">
            <div>
              <span> {{ expectedReturn | denomFormat }}</span>
              <span class="darkgray--text text-6">
                {{ vaultInfo.token | uppercase }}</span
              >
            </div>
            <div class="ml-1">
              (<span class="text-6 font-weight-medium"
                >${{
                  (expectedReturn * getTokenPriceInUsd(vaultInfo.token))
                    | currency
                }}</span
              ><span class="text-6 gray--text ml-1">USD</span>)
            </div>
          </div>
        </div>
        <div class="d-flex justify-space-between mt-2">
          <div class="d-flex align-center">
            <span class="gray--text text-6 font-weight-medium"
              >Total Assets</span
            >
            <v-tooltip top max-width="550" color="dark">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  class="ml-1 icongray--text"
                  v-bind="attrs"
                  v-on="on"
                  >fas fa-info-circle</v-icon
                >
              </template>
              <div>
                Your amount of Total Deposited + Available Assets
              </div>
            </v-tooltip>
          </div>
          <div class="d-flex">
            <div>
              <span> {{ totalAssets | denomFormat }}</span>
              <span class="darkgray--text text-6">
                {{ vaultInfo.token | uppercase }}</span
              >
            </div>
            <div class="ml-1">
              (<span class="text-6 font-weight-medium"
                >${{ totalAssetsUsd | currency }}</span
              ><span class="text-6 gray--text ml-1">USD</span>)
            </div>
          </div>
        </div>
      </div>
      <div class="mt-8">
        <div class="mt-4">
          <c-btn
            class="width-100"
            is-secondary
            :to="{
              name: 'VaultDetails',
              params: { vault: vaultInfo.identity.toLowerCase() },
              query: { action: 'withdraw' },
            }"
            walletRequired
            :disabled="vaultInfo.withdrawalDisabled"
            >Withdraw</c-btn
          >
        </div>
        <div v-if="vaultInfo.migrationRequired" class="mt-4">
          <c-btn
            class="width-100"
            :to="{
              name: 'VaultDetails',
              params: { vault: vaultInfo.identity.toLowerCase() },
              query: { action: 'migrate' },
            }"
            walletRequired
            >Migrate</c-btn
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BN from "bignumber.js";

export default {
  props: {
    vaultInfo: Object,
  },
  computed: {
    ...mapGetters({
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      balanceState: "vaults/coinBalances",
      vaultBalanceState: "vaults/vaultBalances",
    }),
    availableBalances() {
      const tokens = this.vaultInfo.tokens || [this.vaultInfo];
      return tokens.map(item => ({
        name: item.coin,
        balance: this.balanceState.result?.[item.coin]?.value ?? 0,
        value:
          new BN(this.balanceState.result?.[item.coin]?.value ?? 0) *
          this.getTokenPriceInUsd(item.coin),
      }));
    },
    expectedReturn() {
      return this.vaultBalanceState.result?.[this.vaultInfo.vault]?.value || 0;
    },
    shares() {
      return this.vaultBalanceState.result?.[this.vaultInfo.vault]?.shares || 0;
    },
    totalAssets() {
      return new BN(
        this.availableBalances.reduce(
          (acc, cur) => new BN(acc).plus(cur.balance).toNumber(),
          0
        )
      )
        .plus(this.expectedReturn)
        .toNumber();
    },
    totalAssetsUsd() {
      return new BN(this.getTokenPriceInUsd(this.vaultInfo.token))
        .multipliedBy(this.totalAssets)
        .toNumber();
    },
  },
};
</script>
